import { useState, useEffect } from 'react';
import { useDocumentStore } from '../../../store/documentStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, FileText, CheckCircle, Upload, Trash2, ExternalLink } from 'lucide-react';
import { Input } from '../../../components/Input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/Select';
import toast from 'react-hot-toast';

const DOCUMENT_TYPES = [
    { value: 'contract', label: 'Employment Contract' },
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'payslip', label: 'Payslip' },
    { value: 'resume', label: 'Resume / CV' },
    { value: 'other', label: 'Other' },
];

const EMPTY_FORM = { employeeId: '', title: '', type: 'contract', fileUrl: '' };

export function EmployeeDocuments() {
    const { documents, isLoading, fetchDocuments, createDocument, updateDocument, deleteDocument } = useDocumentStore();
    const { employees, fetchEmployees } = useEmployeeStore();
    const [search, setSearch] = useState('');

    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchDocuments();
        // Load the full directory (22 employees today) so the picker is complete.
        fetchEmployees({ limit: 200 });
    }, []);

    const filteredDocuments = documents.filter(d =>
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        `${d.employeeFirstName} ${d.employeeLastName}`.toLowerCase().includes(search.toLowerCase())
    );

    const openForm = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setFormOpen(true);
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.employeeId) next.employeeId = 'Select an employee';
        if (!form.title.trim()) next.title = 'Title is required';
        if (!form.type) next.type = 'Select a document type';
        if (!form.fileUrl.trim()) {
            next.fileUrl = 'Document link is required';
        } else if (!/^https?:\/\/.+/i.test(form.fileUrl.trim())) {
            next.fileUrl = 'Enter a valid URL (https://…)';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await createDocument({
                employeeId: form.employeeId,
                title: form.title.trim(),
                type: form.type,
                fileUrl: form.fileUrl.trim(),
                isVerified: false,
            });
            toast.success('Document added');
            setFormOpen(false);
            // Refetch so the joined employee name is populated on the new row.
            fetchDocuments();
        } catch (e) {
            toast.error('Failed to add document');
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async (id: string) => {
        try {
            await updateDocument(id, { isVerified: true });
            toast.success('Document verified');
        } catch (e) {
            toast.error('Failed to verify document');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteDocument(deleteTarget.id);
            toast.success('Document deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete document');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Employee Documents</h1>
                    <p className="text-muted-foreground">Secure vault for contracts, IDs, and compliance documents.</p>
                </div>
                <Button variant="primary" onClick={openForm}>
                    <Upload className="w-4 h-4 mr-2" />
                    Add Document
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Total Documents</p>
                            <h3 className="text-foreground font-display text-metric">
                                {documents.length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
                            <Search className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Pending Verification</p>
                            <h3 className="text-foreground font-display text-metric">
                                {documents.filter(d => !d.isVerified).length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Verified</p>
                            <h3 className="text-foreground font-display text-metric">
                                {documents.filter(d => d.isVerified).length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Document List</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search documents or employees..."
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Document Title</th>
                                    <th className="px-6 py-3">Employee</th>
                                    <th className="px-6 py-3">Type</th>
                                    <th className="px-6 py-3">Uploaded</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredDocuments.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                                            No documents found.
                                        </td>
                                    </tr>
                                ) : filteredDocuments.map(d => (
                                    <tr key={d.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">
                                            <span className="inline-flex items-center gap-2">
                                                {d.title}
                                                {d.fileUrl && (
                                                    <a
                                                        href={d.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-primary"
                                                        title="Open document"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {d.employeeFirstName || 'Unknown'} {d.employeeLastName || 'Employee'}
                                        </td>
                                        <td className="px-6 py-4 capitalize">{d.type?.replace(/_/g, ' ')}</td>
                                        <td className="px-6 py-4">
                                            {new Date(d.uploadedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            {d.isVerified ? (
                                                <Badge variant="success">Verified</Badge>
                                            ) : (
                                                <Badge variant="warning">Pending</Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {!d.isVerified && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleVerify(d.id)}
                                                        className="text-success hover:text-success/80"
                                                    >
                                                        Verify
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    onClick={() => setDeleteTarget({ id: d.id, title: d.title })}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    aria-label={`Delete ${d.title}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Add Document modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Document</DialogTitle>
                        <DialogDescription>
                            Attach a document to an employee by providing a link to the stored file.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Employee</label>
                            <Select
                                value={form.employeeId}
                                onValueChange={(v) => setForm((f) => ({ ...f, employeeId: v }))}
                            >
                                <SelectTrigger invalid={!!errors.employeeId}>
                                    <SelectValue placeholder="Select an employee" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.displayName || `${e.firstName} ${e.lastName}`} · {e.employeeCode}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.employeeId && <p className="text-xs text-destructive">{errors.employeeId}</p>}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Document Title</label>
                            <Input
                                placeholder="e.g. Employment Contract 2026"
                                value={form.title}
                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                invalid={!!errors.title}
                            />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Type</label>
                            <Select
                                value={form.type}
                                onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                            >
                                <SelectTrigger invalid={!!errors.type}>
                                    <SelectValue placeholder="Select a type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {DOCUMENT_TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.type && <p className="text-xs text-destructive">{errors.type}</p>}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Document link (URL)</label>
                            <Input
                                placeholder="https://…"
                                value={form.fileUrl}
                                onChange={(e) => setForm((f) => ({ ...f, fileUrl: e.target.value }))}
                                invalid={!!errors.fileUrl}
                            />
                            {errors.fileUrl && <p className="text-xs text-destructive">{errors.fileUrl}</p>}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete document</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
